import Decimal from "decimal.js";
import {
  Component,
  createEffect,
  createMemo,
  onCleanup,
  Signal,
  splitProps,
} from "solid-js";
import { formatAmount, formatAmountOnEdit } from "~/schemas/amount";
import { createDebounce } from "./debounce";
import { InputProps } from "./input";

const stripNonDigitsOrDelimiters = (input: string) =>
  input.replace(/[^\d,\.]/g, "");

const AmountInput: Component<
  InputProps<
    string | number | undefined,
    "number" | "text",
    {
      debounce?: number;
      decimalPlaces?: number;
      signal?: Signal<string | number | undefined>;
    }
  >
> = (props) => {
  const [local, inputProps] = splitProps(props, [
    "debounce",
    "decimalPlaces",
    "signal",
    "type",
  ]);
  let ref: HTMLInputElement | undefined;
  createEffect(() => {
    const [_, setValue] = local.signal ?? [];
    const decimalPlaces = local.decimalPlaces ?? 2;
    const debounce = local.debounce ?? 200;
    const el = ref;
    if (setValue === undefined || el === undefined) {
      return;
    }
    const onChange = () => {
      const value = stripNonDigitsOrDelimiters(el.value);
      const amount = formatAmount(value);
      const fixed = new Decimal(amount).toFixed(decimalPlaces);
      setValue(fixed);
    };
    const lazyUpdate = createDebounce((value: string) => {
      const d = new Decimal(value);
      const fixed = d.toFixed(2);
      setValue(d.equals(fixed) ? value : fixed);
    }, debounce);

    const onInput = () => {
      const value = stripNonDigitsOrDelimiters(el.value);
      const amount = formatAmountOnEdit(value);
      lazyUpdate(amount);
    };
    el.addEventListener("input", onInput);
    el.addEventListener("change", onChange);
    onCleanup(() => {
      el.removeEventListener("input", onInput);
      el.removeEventListener("change", onChange);
    });
  });
  const value = createMemo(() => {
    const [value] = local.signal ?? [];
    return value?.();
  });

  return <input ref={ref} value={value()} type="text" {...inputProps} />;
};

export default AmountInput;
